import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { getFullImageUrl } from "../../utils/imageHandler";
import { getJobPath } from "../../utils/jobSlug";
import { glassCard, glassCardHover } from "../../utils/glassStyles";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../components/ui/tabs";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
	Briefcase,
	Heart,
	Clock,
	CheckCircle,
	XCircle,
	AlertCircle,
	Loader2,
	Trash2,
	Eye,
	MapPin,
	Building2,
	Calendar,
} from "lucide-react";
import api from "../../api/client";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import Loader from "../../components/ui/Loader";
export default function ApplierDashboard() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = searchParams.get("tab") === "saved" ? "saved" : "applications";

	const [applications, setApplications] = useState([]);
	const [savedJobs, setSavedJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [actionLoading, setActionLoading] = useState({});

	useEffect(() => {
		fetchDashboardData();
	}, []);

	const fetchDashboardData = async () => {
		try {
			const [appsRes, savedRes] = await Promise.all([
				api.get("/applier/applications"),
				api.get("/applier/saved"),
			]);

			setApplications(appsRes.data || []);
			setSavedJobs(savedRes.data || []);
		} catch (err) {
			console.error(err);
			setError("Failed to load dashboard. Please try again later.");
		} finally {
			setLoading(false);
		}
	};

	const handleWithdraw = async (appId) => {
		setActionLoading((prev) => ({ ...prev, [appId]: true }));
		try {
			await api.delete(`/applier/applications/${appId}`);
			setApplications((prev) => prev.filter((a) => a.id !== appId));
		} catch (err) {
			console.error(err);
			setError(
				err.response?.data?.message ||
				err.message ||
				"Failed to withdraw application. Please try again."
			);
		} finally {
			setActionLoading((prev) => ({ ...prev, [appId]: false }));
		}
	};

	const handleRemoveSaved = async (jobId) => {
		setActionLoading((prev) => ({ ...prev, [`save-${jobId}`]: true }));
		try {
			await api.delete(`/applier/save/${jobId}`);
			setSavedJobs((prev) => prev.filter((j) => j.id !== jobId));
		} catch (err) {
			console.error(err);
			setError("Failed to remove saved job. Please try again.");
		} finally {
			setActionLoading((prev) => ({ ...prev, [`save-${jobId}`]: false }));
		}
	};

	const getStatusBadge = (status) => {
		const map = {
			PENDING: { variant: "secondary", icon: Clock, text: "Pending" },
			REVIEWED: { variant: "outline", icon: AlertCircle, text: "In Review" },
			ACCEPTED: { variant: "default", icon: CheckCircle, text: "Accepted" },
			REJECTED: { variant: "destructive", icon: XCircle, text: "Rejected" },
		};
		const s = map[status] || map.PENDING;
		const Icon = s.icon;
		return (
			<Badge variant={s.variant} className="flex items-center gap-1">
				<Icon className="w-3 h-3" />
				{s.text}
			</Badge>
		);
	};

	if (loading) return <Loader />;

	return (
		<div className="applier-page">
			<div className="applier-page-inner">
				{/* Header */}
				<div className="mb-5 sm:mb-8">
					<div className="flex flex-col gap-3">
						<div>
							<h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold mb-1 tracking-tight leading-tight">
								My{" "}
								<span className="text-brand-gradient">
									Dashboard
								</span>
							</h1>
							<p className="text-xs sm:text-sm text-muted-foreground">
								Track your applications and saved jobs
							</p>
						</div>
						<Button
							onClick={() => navigate("/")}
							className="w-full sm:w-auto sm:self-start gap-2 rounded-xl h-10"
						>
							<Briefcase className="w-4 h-4" />
							Browse Jobs
						</Button>
					</div>
				</div>

				{/* Error Alert */}
				{error && (
					<Alert variant="destructive" className="mb-6 shadow-md">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				{/* Quick Stats */}
				<div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-5 sm:mb-8">
					{/* Total Applications */}
					<Card className={`${glassCard} transition-all duration-300 group`}>
						<CardContent className="p-3 sm:p-4">
							<div className="flex flex-col gap-2">
								<div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-primary/10 flex items-center justify-center">
									<Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
								</div>
								<div>
									<p className="text-[10px] sm:text-xs font-medium text-muted-foreground mb-0.5">
										Applications
									</p>
									<p className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
										{applications.length}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Pending */}
					<Card className={`${glassCard} transition-all duration-300`}>
						<CardContent className="p-3 sm:p-4">
							<div className="flex flex-col gap-2">
								<div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
									<Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 dark:text-yellow-500" />
								</div>
								<div>
									<p className="text-[10px] sm:text-xs font-medium text-muted-foreground mb-0.5">Pending</p>
									<p className="text-xl sm:text-2xl lg:text-3xl font-bold">{applications.filter((a) => a.status === "PENDING").length}</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Accepted */}
					<Card className={`${glassCard} transition-all duration-300`}>
						<CardContent className="p-3 sm:p-4">
							<div className="flex flex-col gap-2">
								<div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
									<CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-500" />
								</div>
								<div>
									<p className="text-[10px] sm:text-xs font-medium text-muted-foreground mb-0.5">Accepted</p>
									<p className="text-xl sm:text-2xl lg:text-3xl font-bold">{applications.filter((a) => a.status === "ACCEPTED").length}</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Saved Jobs */}
					<Card className={`${glassCard} transition-all duration-300`}>
						<CardContent className="p-3 sm:p-4">
							<div className="flex flex-col gap-2">
								<div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
									<Heart className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-500" />
								</div>
								<div>
									<p className="text-[10px] sm:text-xs font-medium text-muted-foreground mb-0.5">Saved</p>
									<p className="text-xl sm:text-2xl lg:text-3xl font-bold">{savedJobs.length}</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Tabs */}
				<Tabs
					value={activeTab}
					onValueChange={(value) => {
						if (value === "saved") setSearchParams({ tab: "saved" });
						else setSearchParams({});
					}}
					className="w-full"
				>
					<TabsList className="mb-4 sm:mb-6 w-full grid grid-cols-2 h-10 p-1 rounded-xl">
						<TabsTrigger
							value="applications"
							className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm h-9 w-full gap-1.5"
						>
							<Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 pointer-events-none" />
							<span className="hidden sm:inline pointer-events-none">
								Applications
							</span>
							<span className="sm:hidden pointer-events-none">Apps</span>
							<span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold pointer-events-none">
								{applications.length}
							</span>
						</TabsTrigger>
						<TabsTrigger
							value="saved"
							className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm h-9 w-full gap-1.5"
						>
							<Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 pointer-events-none" />
							<span className="pointer-events-none">Saved</span>
							<span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold pointer-events-none">
								{savedJobs.length}
							</span>
						</TabsTrigger>
					</TabsList>

					{/* Applications Tab */}
					<TabsContent value="applications" className="space-y-4">
						{applications.length === 0 ? (
							<Card className="border-2 border-dashed border-muted-foreground/20">
								<CardContent className="text-center py-16 sm:py-20">
									<div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mb-6">
										<Briefcase className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
									</div>
									<p className="text-lg sm:text-xl font-semibold text-foreground mb-2">
										No applications yet
									</p>
									<p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-md mx-auto">
										Start applying to jobs and track your progress here
									</p>
									<Button
										onClick={() => navigate("/")}
										size="lg"
										className="gap-2 shadow-lg"
									>
										<Briefcase className="w-4 h-4" />
										Browse Jobs
									</Button>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 gap-4">
								{applications.map((app) => {
									const job = app.job;
									if (!job) {
										return (
											<Card
												key={app.id}
												className="border-2 border-destructive/20"
											>
												<CardContent className="text-center py-6">
													<XCircle className="w-12 h-12 mx-auto text-destructive mb-3" />
													<p className="text-muted-foreground">
														This job is no longer available.
													</p>
												</CardContent>
											</Card>
										);
									}

									return (
										<Card
											key={app.id}
											className={`${glassCardHover} group`}
										>
											<CardContent className="p-3 sm:p-5">
												<div className="space-y-3">
													<div className="flex items-start gap-3">
														<Avatar className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl border bg-white/80 dark:bg-white/10 shadow-sm overflow-hidden shrink-0">
															{(job.companyLogo || job.recruiter?.logoUrl) && (
																<img 
																	src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)} 
																	alt={job.companyName}
																	className="w-full h-full object-contain p-1"
																/>
															)}
															<AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-black text-sm uppercase">
																{job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
															</AvatarFallback>
														</Avatar>
														<div className="flex-1 min-w-0 space-y-2">
															<div className="flex flex-col gap-1.5">
																<h3 className="text-sm sm:text-base font-semibold text-foreground hover:text-primary transition-colors line-clamp-2 leading-snug">
																	{(job.id || job.jobId) ? (
																		<Link to={getJobPath(job)}>{job.title}</Link>
																	) : (
																		<span>{job.title}</span>
																	)}
																</h3>
																<div className="flex flex-wrap items-center gap-1.5">
																	{getStatusBadge(app.status)}
																	{(job.status === "EXPIRED" || (job.deadline && new Date(job.deadline) < new Date())) && (
																		<Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
																			<Clock className="w-3 h-3" />
																			Expired
																		</Badge>
																	)}
																</div>
															</div>

															<div className="flex flex-col gap-1 text-[11px] sm:text-xs text-muted-foreground">
																<div className="flex items-center gap-1.5">
																	<Building2 className="w-4 h-4 shrink-0" />
																	<span className="truncate">
																		{job.companyName ||
																			job.recruiter?.companyName ||
																			"Unknown Company"}
																	</span>
																</div>
																<div className="flex items-center gap-1.5">
																	<Calendar className="w-4 h-4 shrink-0" />
																	<span>
																		{new Date(app.appliedAt).toLocaleDateString()}
																	</span>
																</div>
																<div className="flex items-center gap-1.5">
																	<MapPin className="w-4 h-4 shrink-0" />
																	<span className="truncate">{job.location}</span>
																</div>
															</div>

															{(job.salaryMin || job.salaryMax) && (
																<div className="flex items-center gap-2 text-sm font-semibold text-primary">
																	<div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center">
																		💰
																	</div>
																	{job.salaryMin
																		? `${job.salaryMin.toLocaleString()} – ${job.salaryMax.toLocaleString()} ETB`
																		: "Competitive Salary"}
																</div>
															)}
														</div>
													</div>

													<div className="flex flex-col gap-2 pt-1 border-t border-border/50">
														{(job.id || job.jobId) && (
															<Link
																to={getJobPath(job)}
																className="inline-block w-full text-center bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
															>
																View Details
															</Link>
														)}

														{app.status === "PENDING" && (
															<ConfirmDialog
																title="Withdraw Application?"
																description="This action cannot be undone. Your application will be permanently removed."
																onConfirm={() => handleWithdraw(app.id)}
																trigger={
																	<Button
																		variant="destructive"
																		size="sm"
																		disabled={actionLoading[app.id]}
																		className="w-full gap-2 rounded-xl h-10"
																	>
																		{actionLoading[app.id] ? (
																			<Loader2 className="w-4 h-4 animate-spin" />
																		) : (
																			<Trash2 className="w-4 h-4" />
																		)}
																		<span>Withdraw</span>
																	</Button>
																}
															/>
														)}
													</div>
												</div>
											</CardContent>
										</Card>
									);
								})}
							</div>
						)}
					</TabsContent>

					{/* Saved Jobs Tab */}
					<TabsContent value="saved" className="space-y-4">
						{savedJobs.length === 0 ? (
							<Card className="border-2 border-dashed border-muted-foreground/20">
								<CardContent className="text-center py-16 sm:py-20">
									<div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mb-6">
										<Heart className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
									</div>
									<p className="text-lg sm:text-xl font-semibold text-foreground mb-2">
										No saved jobs yet
									</p>
									<p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-md mx-auto">
										Save interesting jobs to come back to later
									</p>
									<Button
										onClick={() => navigate("/")}
										size="lg"
										className="gap-2 shadow-lg"
									>
										<Briefcase className="w-4 h-4" />
										Explore Jobs
									</Button>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 gap-4">
								{savedJobs.map((job) => (
									<Card
										key={job.id || job.jobId}
										className={`${glassCardHover} group cursor-pointer`}
										onClick={() => {
											const path = getJobPath(job);
											if (path !== "/") navigate(path);
										}}
									>
										<CardContent className="p-3 sm:p-5">
											<div className="space-y-3">
												<div className="flex items-start gap-3">
													<Avatar className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl border bg-white/80 dark:bg-white/10 shadow-sm overflow-hidden shrink-0">
														{(job.companyLogo || job.recruiter?.logoUrl) && (
															<img 
																src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)} 
																alt={job.companyName}
																className="w-full h-full object-contain p-1"
															/>
														)}
														<AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-black text-sm uppercase">
															{job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
														</AvatarFallback>
													</Avatar>
													<div className="flex-1 min-w-0 space-y-2">
														<div className="flex flex-col gap-1.5">
															<h3 className="text-sm sm:text-base font-semibold hover:text-primary transition-colors line-clamp-2 leading-snug">
																{job.title || "Job not available"}
															</h3>
															<div className="flex flex-wrap items-center gap-1.5">
																	<Badge className="bg-red-50 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 border flex items-center gap-1.5 w-fit">
																		<Heart className="w-3 h-3 fill-current" />
																		<span className="hidden sm:inline">Saved</span>
																	</Badge>
																	{(job.status === "EXPIRED" || (job.deadline && new Date(job.deadline) < new Date())) && (
																		<Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
																			<Clock className="w-3 h-3" />
																			Expired
																		</Badge>
																	)}
																</div>
															</div>

														<div className="flex flex-col gap-1 text-[11px] sm:text-xs text-muted-foreground">
															<div className="flex items-center gap-1.5">
																<Building2 className="w-4 h-4 shrink-0" />
																<span className="truncate">
																	{job.companyName ||
																		job.recruiter?.companyName ||
																		"Unknown Company"}
																</span>
															</div>
															<div className="flex items-center gap-1.5">
																<MapPin className="w-4 h-4 shrink-0" />
																<span className="truncate">
																	{job.location || "Unknown"}
																</span>
															</div>
															<div className="flex items-center gap-1.5">
																<Briefcase className="w-4 h-4 shrink-0" />
																<span>{job.type || "Full-time"}</span>
															</div>
														</div>

														{(job.salaryMin || job.salaryMax) && (
															<div className="flex items-center gap-2 text-sm font-semibold text-primary">
																<div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center">
																	💰
																</div>
																{job.salaryMin
																	? `${job.salaryMin.toLocaleString()} – ${job.salaryMax.toLocaleString()} ETB`
																	: "Competitive Salary"}
															</div>
														)}
													</div>
												</div>

												<div
													className="flex flex-col gap-2 pt-1 border-t border-border/50"
													onClick={(e) => e.stopPropagation()}
												>
													<Button
														size="sm"
														onClick={(e) => {
															e.stopPropagation();
															const path = getJobPath(job);
															if (path !== "/") navigate(path);
														}}
														className="w-full gap-2 rounded-xl h-10"
													>
														<Eye className="w-4 h-4" />
														View Details
													</Button>

													<ConfirmDialog
														title="Remove Saved Job?"
														description="This job will be removed from your saved list. You can save it again anytime."
														onConfirm={() => handleRemoveSaved(job.id)}
														trigger={
															<Button
																variant="outline"
																size="sm"
																disabled={actionLoading[`save-${job.id}`]}
																className="w-full gap-2 rounded-xl h-10"
																onClick={(e) => e.stopPropagation()}
															>
																{actionLoading[`save-${job.id}`] ? (
																	<Loader2 className="w-4 h-4 animate-spin" />
																) : (
																	<Trash2 className="w-4 h-4" />
																)}
																<span>Remove</span>
															</Button>
														}
													/>
												</div>
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
